import React, { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Button, Text } from '@components/common';
import { Preview as MarkdownPreview } from '@components/common/MarkdownEditor';
import { RecordingIndicator } from '@components/ScreenSharingProvider/components/RecordingIndicator';
import { BLOCK_KIND_LABELS } from '@components/SurveyBuilder/constants';
import * as Models from '@components/SurveyBuilder/types/models';
import { CollapseSVG, ExpandSVG } from '@components/svgs';
import { useUnmoderatedContext } from '@components/Unmoderated/hooks/useUnmoderatedContext';
import { useMutationObserver } from '@hooks/useMutationObserver';

interface Props {
  block: Models.Block;
  renderOptions: () => React.JSX.Element;
  isMinimized: boolean;
  setIsMinimized: (isMinimized: boolean) => void;
  renderQuestion?: () => React.JSX.Element;
}

export const TaskHeader: FC<Props> = ({ block, renderOptions, setIsMinimized, isMinimized, renderQuestion }) => {
  const { blocks } = useUnmoderatedContext();

  const { description, title, position, kind } = block;

  const [maxHeight, setMaxHeight] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const animationContainerRef = useRef<HTMLDivElement>(null);

  // Use a ref to store the current isMinimized value so mutation observer can access it
  const isMinimizedRef = useRef(isMinimized);
  isMinimizedRef.current = isMinimized;

  const recalcHeight = useCallback(() => {
    if (isMinimizedRef.current) {
      setMaxHeight('0px');
      return;
    }

    if (descriptionRef.current) {
      setMaxHeight(`${descriptionRef.current.scrollHeight}px`);
    }
  }, []);

  useLayoutEffect(() => {
    recalcHeight();
  }, [isMinimized, description, recalcHeight]);

  // Use the stabilized callback for mutation observer
  useMutationObserver(descriptionRef, recalcHeight, {
    childList: true,
    subtree: true,
    characterData: true
  });

  // Handle animation start/end to control overflow during transitions
  const handleTransitionStart = () => {
    setIsAnimating(true);
  };

  const handleTransitionEnd = () => {
    setIsAnimating(false);
  };

  // Add transition event listeners
  useLayoutEffect(() => {
    const container = animationContainerRef.current;
    if (!container) return;

    container.addEventListener('transitionstart', handleTransitionStart);
    container.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      container.removeEventListener('transitionstart', handleTransitionStart);
      container.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, []);

  return (
    <div className='max-h-2/3 relative flex w-full border-b border-gray-200 bg-white shadow-md' style={{ zIndex: 1 }}>
      <div className='flex flex-1 self-start justify-self-start px-6 pb-4'>
        <RecordingIndicator />
      </div>
      <div className='flex h-full w-1/2 max-w-3xl flex-col justify-start self-center'>
        <div
          className='overflow-hidden px-4 duration-300 ease-in-out'
          style={{ maxHeight: isMinimized ? '0px' : '50px', minHeight: isMinimized ? '0px' : '26px' }}
        >
          <div className='mb-2 flex items-center space-x-4'>
            <Text className='text-custom-brand text-xs' uppercase bold>
              {BLOCK_KIND_LABELS[kind]}
            </Text>

            <Text className='text-custom-brand text-xs'>
              {position} / {blocks.length}
            </Text>
          </div>
        </div>

        <Text h='600' className={cn('text-custom-brand px-4', isMinimized && 'truncate')} bold>
          {title}
        </Text>

        <div
          ref={animationContainerRef}
          className='relative overflow-hidden duration-300 ease-in-out'
          style={{ maxHeight }}
        >
          <div
            ref={descriptionRef}
            className={cn(
              'flex max-h-full flex-col px-4',
              // Only show scrollbar when not animating and not minimized
              {
                'mt-2 pb-10': renderQuestion || description,
                'pb-4': !renderQuestion && !description,
                'overflow-hidden': isAnimating || isMinimized,
                'overflow-y-auto': !isAnimating && !isMinimized
              }
            )}
          >
            {description && (
              <MarkdownPreview
                components={{
                  a: ({ children, href }) => (
                    <a href={href} target='_blank' rel='noopener noreferrer'>
                      {children}
                    </a>
                  )
                }}
              >
                {description}
              </MarkdownPreview>
            )}
            {renderQuestion?.()}
          </div>
          <div className='pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent' />
        </div>
      </div>

      <div className='flex flex-1 flex-nowrap justify-end self-start px-6 pb-4'>
        {renderOptions()}
        <Button
          className='ml-3'
          type='button'
          aria-label={isMinimized ? 'Expand' : 'Collapse'}
          onClick={() => setIsMinimized(!isMinimized)}
        >
          {isMinimized ? <ExpandSVG /> : <CollapseSVG />}
        </Button>
      </div>
    </div>
  );
};
